/**
 * PDF 導出工具
 * 專門用於圖片畫廊的 A4 PDF 導出（支持橫向/縱向）
 */

import { TimezoneUtils } from '../../../../utils/timezoneUtils';
import { normalizeStatusForDisplay } from './mediaGrouping';
import { filterGroupedMediaBySelection } from './filterExcludedMedia';
import { getPvDisplayLabel, buildFullGroupTitle } from './exportMediaShared';

// A4 尺寸（mm）
const A4_WIDTH_LANDSCAPE = 297; // 橫向寬度
const A4_HEIGHT_LANDSCAPE = 210; // 橫向高度
const A4_WIDTH_PORTRAIT = 210; // 縱向寬度
const A4_HEIGHT_PORTRAIT = 297; // 縱向高度

// 邊距（mm）
const MARGIN = 10;

/**
 * 將圖片 URL 轉換為完整 URL（處理相對路徑）
 * @param {string} filePath - 圖片文件路徑
 * @returns {string} 完整的圖片 URL
 */
const getImageUrl = (filePath) => {
  if (!filePath) return '';
  
  if (filePath.startsWith('http://') || filePath.startsWith('https://') || filePath.startsWith('data:')) {
    return filePath;
  }
  
  let normalizedPath = filePath;
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = '/' + normalizedPath;
  }
  
  if (normalizedPath.startsWith('/Uploads')) {
    return normalizedPath;
  }
  
  return `/Uploads${normalizedPath}`;
};

/**
 * 將文本渲染為圖片（支持中文）
 * @param {string} text - 要渲染的文本
 * @param {number} fontSize - 字體大小（px）
 * @param {string} fontFamily - 字體家族
 * @param {string} fontWeight - 字體粗細（normal/bold）
 * @param {string} color - 文本顏色（hex）
 * @returns {Promise<string>} 圖片數據 URL
 */
const renderTextToImage = (text, fontSize = 12, fontFamily = 'Arial, sans-serif', fontWeight = 'normal', color = '#000000') => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // 設置字體
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'top';
    
    // 測量文本寬度
    const textMetrics = ctx.measureText(text);
    const textWidth = textMetrics.width;
    const textHeight = fontSize * 1.2; // 增加一些高度以容納字符
    
    // 設置 canvas 尺寸（添加一些 padding）
    canvas.width = Math.ceil(textWidth) + 10;
    canvas.height = Math.ceil(textHeight) + 10;
    
    // 重新設置字體（canvas 尺寸改變後需要重新設置）
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'top';
    
    // 繪製文本
    ctx.fillText(text, 5, 5);
    
    resolve(canvas.toDataURL('image/png'));
  });
};

/**
 * 檢查字符串是否包含非 ASCII 字符（可能包含中文）
 * @param {string} str - 要檢查的字符串
 * @returns {boolean} 是否包含非 ASCII 字符
 */
const hasNonAsciiChars = (str) => {
  return /[^\x00-\x7F]/.test(str);
};

/**
 * 將文本添加到 PDF（自動處理中文）
 * @param {Object} pdf - jsPDF 實例
 * @param {string} text - 要添加的文本
 * @param {number} x - X 坐標（mm）
 * @param {number} y - Y 坐標（mm）
 * @param {Object} options - 選項 {fontSize, fontFamily, fontWeight, color, align}
 * @returns {Promise<number>} 文本高度（mm）
 */
const addTextToPdf = async (pdf, text, x, y, options = {}) => {
  const {
    fontSize = 12,
    fontFamily = 'Arial, sans-serif',
    fontWeight = 'normal',
    color = '#000000',
    align = 'left'
  } = options;

  // 如果包含非 ASCII 字符（可能包含中文），使用圖片渲染
  if (hasNonAsciiChars(text)) {
    try {
      // html2canvas 已在頂部導入，這裡需要動態導入（因為函數可能被調用時模塊尚未加載）
      const { default: html2canvas } = await import('html2canvas');
      
      // 創建臨時 DOM 元素
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.fontSize = `${fontSize}px`;
      tempDiv.style.fontFamily = fontFamily;
      tempDiv.style.fontWeight = fontWeight;
      tempDiv.style.color = color;
      tempDiv.style.whiteSpace = 'nowrap';
      tempDiv.textContent = text;
      document.body.appendChild(tempDiv);
      
      // 使用 html2canvas 渲染
      const canvas = await html2canvas(tempDiv, {
        backgroundColor: null,
        scale: 2,
        logging: false
      });
      
      // 清理臨時元素
      document.body.removeChild(tempDiv);
      
      // 轉換為圖片並添加到 PDF
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = (canvas.width * 0.264583); // px to mm (96 DPI)
      const imgHeight = (canvas.height * 0.264583);
      
      // 根據對齊方式調整 X 坐標
      let adjustedX = x;
      if (align === 'center') {
        adjustedX = x - imgWidth / 2;
      } else if (align === 'right') {
        adjustedX = x - imgWidth;
      }
      
      pdf.addImage(imgData, 'PNG', adjustedX, y, imgWidth, imgHeight);
      return imgHeight;
    } catch (error) {
      console.error('渲染中文文本失敗，回退到英文:', error);
      // 如果失敗，嘗試使用英文替代或直接使用 pdf.text（會顯示亂碼）
      pdf.setFontSize(fontSize);
      pdf.setTextColor(color);
      pdf.text(text, x, y, { align });
      return fontSize * 0.352778; // 估算高度（mm）
    }
  } else {
    // 純 ASCII 文本，直接使用 pdf.text
    pdf.setFontSize(fontSize);
    pdf.setTextColor(color);
    pdf.text(text, x, y, { align });
    return fontSize * 0.352778; // 估算高度（mm）
  }
};

/**
 * 加載圖片並獲取尺寸和數據 URL
 * @param {string} imageUrl - 圖片 URL
 * @returns {Promise<{width: number, height: number, image: Image, dataUrl: string}>} 圖片尺寸、對象和數據 URL
 */
const loadImage = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        // 將圖片轉換為 canvas，然後轉換為 data URL
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
          image: img,
          dataUrl: dataUrl
        });
      } catch (error) {
        reject(new Error(`圖片處理失敗: ${imageUrl}, ${error.message}`));
      }
    };
    img.onerror = (error) => {
      reject(new Error(`圖片加載失敗: ${imageUrl}`));
    };
    img.src = imageUrl;
  });
};

/**
 * 計算圖片在 PDF 中的尺寸
 * @param {number} imgWidth - 圖片原始寬度
 * @param {number} imgHeight - 圖片原始高度
 * @param {number} maxWidth - 最大寬度（mm）
 * @param {number} maxHeight - 最大高度（mm）
 * @returns {{width: number, height: number}} 調整後的尺寸（mm）
 */
const calculateImageSize = (imgWidth, imgHeight, maxWidth, maxHeight) => {
  const ratio = imgWidth / imgHeight;
  
  let width = maxWidth;
  let height = maxWidth / ratio;
  
  if (height > maxHeight) {
    height = maxHeight;
    width = maxHeight * ratio;
  }
  
  return { width, height };
};

/**
 * 導出圖片畫廊為 PDF
 * @param {Array} groupedMedia - 分組後的媒體數據
 * @param {Object} settings - 導出設置
 * @param {string} settings.orientation - 方向：'landscape'（橫向）或 'portrait'（縱向）
 * @param {number} settings.maxImagesPerPage - 每頁最多圖片數量
 * @param {number} settings.imageGap - 圖片間距（mm）
 * @param {number} settings.minImageWidth - 最小圖片寬度（mm）
 * @param {string} fileName - 文件名（不含擴展名）
 * @param {Function} onProgress - 進度回調函數 (current, total, message)
 * @returns {Promise<{success: boolean, fileName: string}>}
 */
export const exportGalleryToPdf = async (
  groupedMedia,
  settings = {},
  fileName = 'apps_media_reports',
  onProgress = null,
  groupingPvs = [],
  selectedByGroup = null,
  pvDefinitions = [],
  groupingEformFields = []
) => {
  try {
    const mediaForExport = selectedByGroup && Object.keys(selectedByGroup).length > 0
      ? filterGroupedMediaBySelection(groupedMedia, selectedByGroup)
      : groupedMedia;
    // 動態導入 jsPDF 和 html2canvas
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas')
    ]);

    const {
      orientation = 'landscape', // 'landscape' 或 'portrait'
      maxImagesPerPage = 4,
      imageGap = 5,
      minImageWidth = 50
    } = settings;

    // 確定 A4 尺寸
    const pageWidth = orientation === 'landscape' ? A4_WIDTH_LANDSCAPE : A4_WIDTH_PORTRAIT;
    const pageHeight = orientation === 'portrait' ? A4_HEIGHT_PORTRAIT : A4_HEIGHT_LANDSCAPE;
    const availableWidth = pageWidth - MARGIN * 2;
    /** 內容區下緣（絕對 Y，mm），預留頁腳 */
    const FOOTER_RESERVE_MM = 14;
    const contentBottomY = pageHeight - MARGIN - FOOTER_RESERVE_MM;

    // 創建 PDF 實例
    const pdf = new jsPDF(orientation, 'mm', 'a4');
    let currentY = MARGIN;

    // 計算總子組數（用於進度報告）
    const totalSubGroups = mediaForExport.reduce((sum, group) => sum + (group.subGroups?.length || 0), 0);
    let processedSubGroups = 0;
    let currentRowY = MARGIN; // 初始化 currentRowY

    // 處理每個 PV 組合
    for (const group of mediaForExport) {
      const { processVariables, subGroups = [] } = group;

      // 添加分組標題（PV + eForm 欄位組合）
      const groupTitle = buildFullGroupTitle(group, groupingPvs, pvDefinitions, groupingEformFields);
      if (groupTitle) {
        if (currentY > contentBottomY - 35) {
          pdf.addPage();
          currentY = MARGIN;
        }
        const textHeight = await addTextToPdf(pdf, groupTitle, MARGIN, currentY, {
          fontSize: 12,
          fontWeight: 'bold'
        });
        currentY += textHeight + 2;
      }

      // 處理每個狀態子組
      for (const subGroup of subGroups) {
        if (onProgress) {
          const normalizedStatus = normalizeStatusForDisplay(subGroup.status);
          onProgress(processedSubGroups, totalSubGroups, `正在處理分組: ${subGroup.workflowExecution?.workflowName || '未知'} (${normalizedStatus})`);
        }

        const { workflowExecution, mediaFiles, executions = [] } = subGroup;

        if (currentY > contentBottomY - 35) {
          pdf.addPage();
          currentY = MARGIN;
        }

        // 添加狀態子組標題
        const normalizedStatus = normalizeStatusForDisplay(subGroup.status);
        const statusTitle = `狀態: ${normalizedStatus}${executions.length > 1 ? ` (${executions.length} 個實例)` : workflowExecution?.id ? ` (ID: ${workflowExecution.id})` : ''}`;
        const titleHeight = await addTextToPdf(pdf, statusTitle, MARGIN, currentY, {
          fontSize: 11,
          fontWeight: 'bold'
        });
        currentY += titleHeight + 2;

        currentY += 2; // 添加間距

        // 固定欄數網格：每列寬度 = (可用寬度 - 欄間距) / 欄數（與畫廊「每頁 N 張橫排」一致）
        const cols = Math.max(1, Math.min(Number(maxImagesPerPage) || 4, 12));
        const cellMaxWidth = (availableWidth - (cols - 1) * imageGap) / cols;

        currentRowY = currentY;
        let colInRow = 0;
        let rowMaxHeight = 0;

        const startNewPageForImages = () => {
          pdf.addPage();
          currentRowY = MARGIN;
          colInRow = 0;
          rowMaxHeight = 0;
        };

        for (let i = 0; i < mediaFiles.length; i++) {
          const mediaFile = mediaFiles[i];
          const imageUrl = getImageUrl(mediaFile.filePath);

          try {
            let imgWidth;
            let imgHeight;
            let imgDataUrl;
            try {
              const imgData = await loadImage(imageUrl);
              imgWidth = imgData.width;
              imgHeight = imgData.height;
              imgDataUrl = imgData.dataUrl;
            } catch (imgError) {
              console.error(`圖片加載失敗: ${imageUrl}`, imgError);
              continue;
            }

            // 新一列的第一張：先確保本列有足夠垂直空間
            if (colInRow === 0 && contentBottomY - currentRowY < 18) {
              startNewPageForImages();
            }

            let maxRowHeight = contentBottomY - currentRowY;
            let { width: pdfWidth, height: pdfHeight } = calculateImageSize(
              imgWidth,
              imgHeight,
              cellMaxWidth,
              maxRowHeight
            );

            let finalWidth = Math.min(
              Math.max(pdfWidth, Math.min(minImageWidth, cellMaxWidth)),
              cellMaxWidth
            );
            let finalHeight = (finalWidth / pdfWidth) * pdfHeight;
            if (finalHeight > maxRowHeight) {
              finalHeight = maxRowHeight;
              finalWidth = (finalHeight / pdfHeight) * pdfWidth;
            }

            // 若本張圖底部超出內容區：先結束當前列再換頁
            if (currentRowY + finalHeight > contentBottomY) {
              if (colInRow > 0) {
                currentRowY += rowMaxHeight + imageGap;
                colInRow = 0;
                rowMaxHeight = 0;
              }
              if (contentBottomY - currentRowY < 18) {
                startNewPageForImages();
              }
              maxRowHeight = contentBottomY - currentRowY;
              const refit = calculateImageSize(imgWidth, imgHeight, cellMaxWidth, maxRowHeight);
              pdfWidth = refit.width;
              pdfHeight = refit.height;
              finalWidth = Math.min(
                Math.max(pdfWidth, Math.min(minImageWidth, cellMaxWidth)),
                cellMaxWidth
              );
              finalHeight = (finalWidth / pdfWidth) * pdfHeight;
              if (finalHeight > maxRowHeight) {
                finalHeight = maxRowHeight;
                finalWidth = (finalHeight / pdfHeight) * pdfWidth;
              }
            }

            const x = MARGIN + colInRow * (cellMaxWidth + imageGap);
            pdf.addImage(imgDataUrl, 'JPEG', x, currentRowY, finalWidth, finalHeight);

            colInRow += 1;
            rowMaxHeight = Math.max(rowMaxHeight, finalHeight);

            if (colInRow >= cols) {
              currentRowY += rowMaxHeight + imageGap;
              colInRow = 0;
              rowMaxHeight = 0;
            }
          } catch (error) {
            console.error(`處理圖片失敗: ${imageUrl}`, error);
          }
        }

        if (colInRow > 0) {
          currentRowY += rowMaxHeight + imageGap;
        }

        currentY = currentRowY;

      processedSubGroups++;
      
      // 在狀態子組之間添加間距
      if (subGroup !== subGroups[subGroups.length - 1]) {
        currentY += 10;
        currentRowY = currentY;
      }
    }
    
    // 在 PV 組合之間添加更大的間距
    if (group !== groupedMedia[groupedMedia.length - 1]) {
      currentY += 15;
      currentRowY = currentY;
    }
  }

  // 添加頁腳（最後一頁）
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    const footerText = `第 ${i} 頁 / 共 ${totalPages} 頁 | 生成時間: ${TimezoneUtils.formatDateTime(new Date(), 'YYYY-MM-DD HH:mm:ss')}`;
    await addTextToPdf(pdf, footerText, pageWidth / 2, pageHeight - 5, {
      fontSize: 8,
      color: '#808080',
      align: 'center'
    });
  }

  // 下載 PDF
  const today = TimezoneUtils.formatDateTime(new Date(), 'YYYY-MM-DD');
  const pdfFileName = `${fileName}_${today}.pdf`;
  pdf.save(pdfFileName);

  if (onProgress) {
    onProgress(totalSubGroups, totalSubGroups, '導出完成');
  }

    return { success: true, fileName: pdfFileName };
  } catch (error) {
    console.error('PDF 導出失敗:', error);
    throw new Error(`PDF 導出失敗: ${error.message}`);
  }
};

export default {
  exportGalleryToPdf
};
